import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData, Link } from "@remix-run/react";
import { useEffect, useState } from "react";
import { ArticleService } from "~/services/article.server";
import { Button } from "~/components/ui/Button";
import { ShareButton } from "~/components/ui/ShareButton";
import { RelatedArticles } from "~/components/articles/RelatedArticles";
import { SEOUtils } from "~/lib/seo-utils";
import { StructuredDataGenerator } from "~/lib/structured-data";
import { createArticleShareContent } from "~/lib/share-utils";

// Article category mapping
const CATEGORY_LABELS = {
  "slow-aging": "저속노화",
  "health-info": "건강정보",
  ingredients: "식재료정보",
  nutrition: "영양정보",
  lifestyle: "라이프스타일",
} as const;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.article) {
    return [
      { title: "아티클을 찾을 수 없습니다 - 슬로우다이브" },
      { name: "description", content: "요청하신 아티클을 찾을 수 없습니다." },
    ];
  }

  const { article } = data;
  return [
    { title: `${article.title} - 슬로우다이브` },
    { name: "description", content: article.excerpt || article.title },
    { property: "og:title", content: article.title },
    { property: "og:description", content: article.excerpt || article.title },
    { property: "og:type", content: "article" },
    ...(article.featuredImageUrl
      ? [{ property: "og:image", content: article.featuredImageUrl }]
      : []),
  ];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params;

  if (!slug) {
    throw new Response("Slug is required", { status: 400 });
  }

  try {
    const article = await ArticleService.getArticleBySlug(slug);

    if (!article || !article.isPublished) {
      throw new Response("Article not found", { status: 404 });
    }

    // Get related articles from the same category
    const relatedArticles = await ArticleService.getArticlesByCategory(
      article.category,
      true
    );

    // Filter out current article and limit to 3
    const filteredRelated = relatedArticles
      .filter((related) => related.id !== article.id)
      .slice(0, 3);

    return json({
      article,
      relatedArticles: filteredRelated,
    });
  } catch (error) {
    console.error("Failed to load article:", error);
    throw new Response("Failed to load article", { status: 500 });
  }
}

export default function ArticleDetail() {
  const { article, relatedArticles } = useLoaderData<typeof loader>();
  const [showBackToTop, setShowBackToTop] = useState(false);

  const categoryLabel =
    CATEGORY_LABELS[article.category as keyof typeof CATEGORY_LABELS];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // HTML 콘텐츠를 안전하게 렌더링하는 함수
  const renderHtmlContent = (htmlContent: string) => {
    return { __html: htmlContent };
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li>
              <Link
                to="/articles"
                className="hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                건강정보
              </Link>
            </li>
            <li>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </li>
            <li>
              <Link
                to={`/articles/category/${article.category}`}
                className="hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {categoryLabel}
              </Link>
            </li>
            <li>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </li>
            <li className="text-gray-900 dark:text-gray-100 font-medium truncate">
              {article.title}
            </li>
          </ol>
        </nav>

        {/* Article Header */}
        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-8">
          {article.featuredImageUrl && (
            <div className="aspect-video bg-gray-200 dark:bg-gray-700">
              <img
                src={article.featuredImageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8">
            {/* Category Badge */}
            <div className="mb-4">
              <Link
                to={`/articles/category/${article.category}`}
                className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 rounded-full hover:bg-blue-200 transition-colors"
              >
                {categoryLabel}
              </Link>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {article.title}
            </h1>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                {article.excerpt}
              </p>
            )}

            {/* Meta Info */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-6 mb-8">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {formatDate(article.publishedAt || article.createdAt)}
                </span>
              </div>

              <div className="flex items-center gap-4">
                {article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-100 rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <ShareButton
                  content={createArticleShareContent({
                    slug: article.slug,
                    title: article.title,
                    excerpt: article.excerpt,
                    category: article.category,
                    featuredImageUrl: article.featuredImageUrl,
                  })}
                  variant="ghost"
                  size="sm"
                />
              </div>
            </div>

            {/* Content */}
            <div
              className="prose prose-lg dark:prose-invert max-w-none"
              role="main"
              aria-label="아티클 내용"
              dangerouslySetInnerHTML={renderHtmlContent(article.content)}
            />
          </div>
        </article>

        {/* Related Articles */}
        <RelatedArticles articles={relatedArticles} />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link to={`/articles/category/${article.category}`}>
            <Button variant="secondary">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {categoryLabel} 목록으로
            </Button>
          </Link>

          <Link to="/articles">
            <Button variant="primary">전체 아티클 보기</Button>
          </Link>
        </div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-400 dark:hover:bg-blue-500 p-3 rounded-full shadow-lg transition-all duration-200 z-50"
            aria-label="맨 위로 이동"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
